import Common from '../common/Common';
import { getLinkTmpl } from '../components/Link';

const escapeSpecialChars = (string) => {
    return string.replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&');
};

const recursiveLnbData = (lnbData, depth = 0, callbackMap = new Map(), idDepthArr = []) => {
    if(!Array.isArray(lnbData)) {
        return lnbData;
    }

    const newLnbData = lnbData.map((v, idx) => {
        if(depth === 0) {
            idDepthArr = [];
        }

        // id
        idDepthArr[depth] = idx;
        const id = idDepthArr.join('-');

        // callback map
        if(typeof v?.onClick === 'function') {
            callbackMap?.set(id, v.onClick);
        }

        let newItem = {...v, id};

        if(Array.isArray(v?.sub) && v?.sub?.length > 0) {
            newItem.sub = recursiveLnbData(v.sub, depth + 1, callbackMap, idDepthArr)?.newLnbData;
        }
        
        return newItem;
    });

    return { newLnbData, callbackMap };
}

/**
 * lnbData에 onClick 옵션을 추가했을 경우 newLnbData(id 추가), callbackMap 생성
 * @param {object} lnbData moduleData.lnbData
 * @param {number} depth 현재 전달받은 lnbData의 depth 값
 * @returns {newLnbData, callbackMap} lnbData
 */
export const setLnbData = (lnbData, depth) => {
    return recursiveLnbData(lnbData, depth);
};

/**
 * lnb markup 생성 함수
 * @param {object} lnbData moduleData.lnbData
 * @param {boolean} isSub 현재 전달받은 lnbData값이 2depth인지 여부(재귀)
 * @param {{d1: number, d2: number}} active active 데이터
 * @returns string 
 */
export const lnbMarkup = ({ lnbData, isSub = false, active }) => {
    if (typeof lnbData === 'undefined') return '';

    const getClass = idx => {
        return !isSub ?
            'ncc-lnb-item m' + (idx + 1) + (active.d1 === idx + 1 ? ' is-active' : '') :
            's' + (idx + 1) + (active.d2 === idx + 1 ? ' is-active' : '');
    };

    const getListItem = (item, idx) => {
        const subList =
            item.sub && item.sub.length > 0 ?
            lnbMarkup({
                lnbData: item.sub,
                isSub: true,
                active: active.d1 === idx + 1 ? active : { d1: 0, d2: 0 }
            }) :
            '';

        return `${getLinkTmpl(item)}${subList}`;
    };

    const com = Common.instance;
    const device = com?.device;
    const list = lnbData
        .map((item, idx) => {
            const isShowDevice = !item?.showDevice || (Array.isArray(item?.showDevice) && item?.showDevice?.includes(device));
            return isShowDevice ? `<li class="${getClass(idx)}">${getListItem(item, idx)}</li>` : '';
        })
        .join('\n ');

    return `<ul class="ncc-lnb-${isSub ? 'item__sub' : 'list'}">${list}</ul>`;
};

/**
 * lnbData와 location.href 비교하여 현재 활성화된 메뉴 데이터(active) 반환
 * @param {object} lnbData cnbConfig(moduleData).lnbData
 * @param {{d1: number; d2: number;}} active 현재 활성화된 메뉴 데이터
 * @returns {{d1: number; d2: number;}} mdata 
 */
export const setMenuActive = (lnbData, active) => {
    const lnb = lnbData || [];
    const activeMenu = [];
    let hasRegexp = false;

    if (typeof active === 'undefined' && Array.isArray(lnb)) {
        const mdata = {
            d1: 0,
            d2: 0
        };
        const href = window.location.href;
        const locPathName = window.location.pathname;

        let i = 0,
            j = 0,
            menu,
            sub,
            urls,
            subUrls,
            isMatch;

        for (; i < lnb.length; ++i) {
            menu = lnb[i];

            if (jQuery.isArray(menu.sub)) {
                for (j = 0; j < menu.sub.length; ++j) {
                    sub = menu.sub[j];

                    if (sub.regex && sub.regex.pattern) {
                        const {
                            pattern,
                            flags
                        } = sub.regex;
                        isMatch = new RegExp(pattern, flags).test(locPathName);

                        if (isMatch) {
                            mdata.d1 = i + 1;
                            mdata.d2 = j + 1;
                            hasRegexp = true;
                            break;
                        }
                    } else {
                        subUrls = [sub.url].concat(sub.otherUrls || []);
                        isMatch = subUrls.some(url => {
                            return new RegExp(escapeSpecialChars(url), 'gi').test(href);
                        });

                        if (isMatch) {
                            mdata.d1 = i + 1;
                            mdata.d2 = j + 1;
                        }
                    }
                }
            }

            // regex
            if (menu.regex && menu.regex.pattern) {
                const {
                    pattern,
                    flags
                } = menu.regex;
                isMatch = new RegExp(pattern, flags).test(locPathName);
                if (isMatch) {
                    mdata.d1 = i + 1;
                    hasRegexp = true;
                    break;
                }
            } else {
                let matchUrl;
                urls = [menu.url].concat(menu.otherUrls || []);

                isMatch = urls.some(url => {
                    if (url === '' || url === '/') {
                        return false;
                    }

                    // 도메인만 존재하는 경로라면 예외처리
                    const match = url.match(/:\/\/(?:www\.)?(.[^/]+)(.*)/);
                    if (match && match.length > 2) {
                        const pathname = match[2];
                        if (pathname === '' || pathname === '/') {
                            return false;
                        }
                    }

                    const m = new RegExp(escapeSpecialChars(url), 'gi').test(href);
                    if (m) {
                        matchUrl = url;
                    }
                    return m;
                });

                if (isMatch) {
                    mdata.d1 = i + 1;
                    activeMenu.push({
                        index: i + 1,
                        matchUrl
                    });
                }
            }
        }

        if (!hasRegexp && activeMenu.length > 1) {
            // 중복 체크가 되었을 경우 가장 긴 url 네임을 가진 인덱스를 활성화 한다.
            const activeMenuTarget = activeMenu.reduce((acc, cur) => {
                const url1 = acc.matchUrl;
                const url2 = cur.matchUrl;
                return url1.length > url2.length ? acc : cur;
            });

            mdata.d1 = activeMenuTarget.index;
        }
        
        return mdata;
    }
    
    return active;
};